import { useState } from "react";
import TypeWriterText from "../../components/TypeWriterText";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronDown, ArrowRight, Shield, Heart, Award, FileCheck, Dna, Users, Globe, MessageCircle } from "lucide-react";
import KennelSEO, { localBusinessSchema, faqSchema } from "./KennelSEO";

const faqData = [
  {
    q: "Czy owczarek niemiecki jest psem rodzinnym?",
    a: "Tak, o ile pochodzi z odpowiedzialnej hodowli i jest prawidlowo prowadzony. Owczarek niemiecki silnie wiaze sie z opiekunem, jest lojalny i komunikatywny. Wymaga jednak konsekwencji, ruchu oraz socjalizacji."
  },
  {
    q: "Jaki powinien byc charakter owczarka niemieckiego?",
    a: "Pewny siebie, zrownowazony, czujny, odwazny i chetny do wspolpracy. Wazna jest stabilna psychika, opanowanie i przewidywalnosc reakcji."
  },
  {
    q: "Czy owczarek niemiecki wymaga duzo ruchu?",
    a: "Tak. Jest psem aktywnym, roboczym i inteligentnym, ktory potrzebuje nie tylko spacerow, ale takze pracy umyslowej, treningow i zajec rozwojowych."
  },
  {
    q: "Czy owczarek niemiecki jest latwy w szkoleniu?",
    a: "Tak, jest jednym z najbardziej podatnych na szkolenie psow. Jego inteligencja i chetnosc do wspolpracy sprawiaja, ze doskonale sprawdza sie w obedience, sporcie kynologicznym i pracy uzytkowej."
  },
  {
    q: "Na co zwrocic uwage przy wyborze hodowli owczarka niemieckiego?",
    a: "Na badania zdrowotne rodzicow, rodowod, warunki chowu miotu, socjalizacje szczeniat oraz podejscie hodowcy do charakteru i uzytkowosci."
  },
  {
    q: "Czy badania HD i ED sa wazne?",
    a: "Tak, bardzo. Profilaktyka i selekcja pod katem zdrowia stawow biodrowych i lokciowych to podstawa w hodowli tej rasy."
  },
  {
    q: "Czy rodowod ma znaczenie przy zakupie szczeniecia?",
    a: "Tak. Rodowod potwierdza pochodzenie psa, pozwala sledzic linie hodowlane i jest podstawa swiadomej pracy hodowlanej."
  },
  {
    q: "Czy owczarek niemiecki nadaje sie do sportu i pracy?",
    a: "Tak, to jedna z najbardziej wszechstronnych ras roboczych. Doskonale sprawdza sie w sporcie kynologicznym, pracy szkoleniowej i uzytkowej."
  },
  {
    q: "Czy owczarek niemiecki to rasa dla kazdego?",
    a: "Nie zawsze. To pies wymagajacy zaangazowania, czasu i swiadomego prowadzenia. Najlepiej sprawdza sie u osob, ktore rozumieja potrzeby psa roboczego."
  },
  {
    q: "Dlaczego psychika jest tak wazna w hodowli owczarka niemieckiego?",
    a: "Poniewaz rasa ta powinna laczyc cechy robocze z opanowaniem i stabilnoscia. Prawidlowa psychika wplywa na codzienne funkcjonowanie psa, jego podatnosc na szkolenie i wspolzycie z rodzina."
  }
];

const breedingPoints = [
  { icon: FileCheck, title: "Rejestracja w ZKwP i FCI", text: "Odpowiedzialni hodowcy sa zarejestrowani w Zwiazku Kynologicznym w Polsce (ZKwP), bedacym czescia FCI. Dzieki temu szczenieta otrzymuja dokumenty rodowodowe, a hodowla dziala zgodnie z regulaminem." },
  { icon: Shield, title: "Badania zdrowotne psow hodowlanych", text: "Kluczowe dla owczarkow niemieckich sa badania stawow biodrowych (HD) i lokciowych (ED), ktore pozwalaja ograniczyc defekty genetyczne i zapewnic zdrowie przyszlym pokoleniom." },
  { icon: Dna, title: "Profil DNA", text: "Coraz czesciej stosowany w celu potwierdzenia tozsamosci i pokrewienstwa psow. To element nowoczesnej, wiarygodnej dokumentacji hodowlanej." },
  { icon: Heart, title: "Socjalizacja szczeniat", text: "Pierwsze tygodnie zycia sa kluczowe. Szczenieta powinny miec kontakt z czlowiekiem i roznymi bodzacami, co buduje prawidlowa psychike i pewnosc siebie." },
  { icon: Award, title: "Prezentacja psow i ich osiagniec", text: "Odpowiedzialni hodowcy chetnie prezentuja swoje psy, rodowody, wyniki wystaw i dorobek hodowlany. Wystawy pomagaja ocenic psy pod katem standardu rasy." },
  { icon: Globe, title: "Strona internetowa hodowli", text: "Profesjonalne hodowle prowadza strony z informacjami o psach hodowlanych, badaniach, wynikach wystaw i aktualnych oraz planowanych miotach." },
  { icon: MessageCircle, title: "Staly kontakt z wlascicielami", text: "Dobry hodowca utrzymuje kontakt z wlascicielami szczeniat po ich adopcji, oferujac wsparcie w wychowaniu, zywieniu i szkoleniu." },
];

const FAQItem = ({ q, a, isOpen, onToggle, idx }) => (
  <div className="border border-[#2A2A2D] hover:border-[#D4AF37]/20 transition-colors" data-testid={`faq-item-${idx}`}>
    <button onClick={onToggle} className="w-full flex items-center justify-between p-6 text-left group" data-testid={`faq-toggle-${idx}`}>
      <span className="text-sm md:text-base text-[#F5F5F5] pr-4 group-hover:text-[#D4AF37] transition-colors">{q}</span>
      <ChevronDown className={`w-4 h-4 text-[#D4AF37] shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
    </button>
    <motion.div
      initial={false}
      animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }}
      transition={{ duration: 0.3 }}
      className="overflow-hidden"
    >
      <p className="px-6 pb-6 text-sm text-[#B8B8B8]/70 leading-relaxed">{a}</p>
    </motion.div>
  </div>
);

const BASE = "https://wolfevomkatzenberg.pl";

const KennelBreed = () => {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="bg-[#0B0B0D] min-h-screen">
      <KennelSEO
        title="Owczarek niemiecki - charakter, cechy rasy i FAQ | Wolfe vom Katzenberg"
        description="Poznaj charakter owczarka niemieckiego, najwazniejsze cechy rasy oraz odpowiedzi na najczesciej zadawane pytania. Sprawdz, na co zwrocic uwage przy wyborze hodowli i szczeniecia."
        canonical={`${BASE}/owczarek-niemiecki`}
        ogImage={`${BASE}/dog_profile.png`}
        schema={[localBusinessSchema, faqSchema(faqData)]}
      />
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-[#141416] border-b border-[#2A2A2D]">
        <div className="absolute inset-0 opacity-[0.02]" style={{ backgroundImage: "repeating-linear-gradient(45deg, #D4AF37 0px, #D4AF37 1px, transparent 1px, transparent 40px)" }} />
        <div className="absolute top-0 right-0 w-[500px] h-[300px] bg-[#D4AF37]/[0.04] rounded-full blur-[100px] -translate-y-1/2 translate-x-1/4" />
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-20 md:py-28 relative">
          <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-[10px] tracking-[0.3em] text-[#D4AF37] block mb-6">
            RASA
          </motion.span>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="font-serif text-[clamp(2rem,5vw,3.5rem)] text-[#F5F5F5] leading-tight mb-8"
            data-testid="breed-page-title"
          >
            Owczarek niemiecki{" "}
            <span className="text-[#D4AF37]">— charakter, cechy rasy i hodowla</span>
          </motion.h1>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="w-16 h-px bg-[#D4AF37]/40 mb-8" />
          <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            className="text-[#B8B8B8] text-sm md:text-base leading-relaxed max-w-3xl"
          >
            Owczarek niemiecki to jedna z najbardziej rozpoznawalnych i cenionych ras psow na swiecie. Jego popularnosc nie wynika jedynie z wygladu — to pies o wyjatkowej inteligencji, silnym charakterze i glebokiej wizi z czlowiekiem. Wszechstronnosc owczarka niemieckiego sprawia, ze doskonale odnajduje sie zarowno jako towarzysz rodziny, jak i w sluzbach mundurowych, sporcie kynologicznym czy pracy uzytkowej.
          </motion.p>
          <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
            className="text-[#B8B8B8]/70 text-sm leading-relaxed max-w-3xl mt-4"
          >
            Odpowiedzialna hodowla owczarka niemieckiego to klucz do zachowania zdrowia, charakteru i stabilnej psychiki kolejnych pokolen tej wyjatkowej rasy.
          </motion.p>
        </div>
      </section>

      {/* Dlaczego owczarek niemiecki jest rasa wyjatkowa */}
      <section className="px-6 md:px-12 py-20 md:py-24">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <span className="text-[10px] tracking-[0.3em] text-[#D4AF37] block mb-4">CECHY RASY</span>
            <h2 className="font-serif text-[clamp(1.4rem,3vw,2.2rem)] text-[#F5F5F5] mb-6">Dlaczego owczarek niemiecki jest rasa wyjatkowa</h2>
            <div className="w-12 h-px bg-[#D4AF37]/30 mb-8" />
            <div className="space-y-5 text-sm text-[#B8B8B8] leading-relaxed">
              <p>Owczarek niemiecki zostal stworzony jako pies roboczy, laczacy w sobie inteligencje, wytrzymalosc, chetnosc do pracy i silna wiez z przewodnikiem. To rasa niezwykle wszechstronna — od psa roboczego i sportowego po wiernego towarzysza rodziny.</p>
              <p>Dobrze prowadzony owczarek niemiecki powinien byc pewny siebie, zrownowazony, stabilny i chetny do wspolpracy. Hodowca, ktory rozumie te rase, dba nie tylko o eksterier, ale przede wszystkim o zdrowie, psychike, uzytkowosc i zgodnosc ze standardem rasy.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Charakter owczarka niemieckiego */}
      <section id="charakter" className="bg-[#141416] px-6 md:px-12 py-20 md:py-24 border-y border-[#2A2A2D]">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <span className="text-[10px] tracking-[0.3em] text-[#D4AF37] block mb-4">CHARAKTER</span>
            <h2 className="font-serif text-[clamp(1.4rem,3vw,2.2rem)] text-[#F5F5F5] mb-6">Charakter owczarka niemieckiego</h2>
            <div className="w-12 h-px bg-[#D4AF37]/30 mb-8" />
            <div className="space-y-5 text-sm text-[#B8B8B8] leading-relaxed">
              <p>Owczarek niemiecki laczy w sobie czujnosc, odwage, opanowanie i podatnosc na szkolenie. Silnie wiaze sie z czlowiekiem — jest lojalny wobec rodziny i przewodnika.</p>
              <p>Dobrze zsocjalizowany owczarek niemiecki jest zrownowazony, komunikatywny, przewidywalny, ale tez czujny i gotowy do dzialania. Wymaga konsekwentnego prowadzenia, prawidlowej socjalizacji i codziennej aktywnosci fizycznej oraz umyslowej.</p>
              <p>Nie znosi nudy i bezczynnosci — najlepiej czuje sie u boku opiekuna, ktory rozumie jego potrzeby robocze i da mu mozliwosc rozwijania naturalnych predyspozycji.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Co wyroznia odpowiedzialna hodowle */}
      <section id="ekspert" className="px-6 md:px-12 py-20 md:py-24">
        <div className="max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-14">
            <span className="text-[10px] tracking-[0.3em] text-[#D4AF37] block mb-4">EKSPERT</span>
            <h2 className="font-serif text-[clamp(1.4rem,3vw,2.2rem)] text-[#F5F5F5] mb-6">Co wyroznia odpowiedzialna hodowle</h2>
            <div className="w-12 h-px bg-[#D4AF37]/30 mb-8" />
            <p className="text-sm text-[#B8B8B8] leading-relaxed max-w-3xl">
              Wybor szczeniecia owczarka niemieckiego to decyzja, przy ktorej warto zwrocic uwage na elementy swiadczace o odpowiedzialnej i profesjonalnej hodowli. Hodowla powinna opierac sie na wiedzy kynologicznej, trosce o zdrowie psow i transparentnosci wobec przyszlych wlascicieli.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-5">
            {breedingPoints.map(({ icon: Icon, title, text }, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
                className="relative bg-gradient-to-br from-[#1A1A1D] to-[#111113] border border-[#2A2A2D]/60 p-7 group overflow-hidden hover:border-[#D4AF37]/30 transition-all duration-500"
                data-testid={`breeding-point-${idx}`}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/[0.03] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute -top-20 -right-20 w-40 h-40 bg-[#D4AF37]/[0.04] rounded-full blur-[60px] opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                <div className="relative flex items-start gap-5">
                  <div className="w-12 h-12 rounded-sm bg-[#D4AF37]/[0.08] border border-[#D4AF37]/20 flex items-center justify-center shrink-0 group-hover:bg-[#D4AF37]/[0.15] group-hover:border-[#D4AF37]/40 group-hover:shadow-[0_0_20px_rgba(212,175,55,0.1)] transition-all duration-500">
                    <Icon className="w-5 h-5 text-[#D4AF37]/70 group-hover:text-[#D4AF37] transition-colors duration-500" />
                  </div>
                  <div>
                    <h3 className="text-[#F5F5F5] text-sm font-medium mb-2.5 group-hover:text-[#D4AF37] transition-colors duration-300">{title}</h3>
                    <p className="text-[#B8B8B8]/50 text-xs leading-relaxed group-hover:text-[#B8B8B8]/70 transition-colors duration-500">{text}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Dlaczego warto kupic szczenie */}
      <section id="hodowla" className="bg-[#141416] px-6 md:px-12 py-20 md:py-24 border-y border-[#2A2A2D]">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <span className="text-[10px] tracking-[0.3em] text-[#D4AF37] block mb-4">HODOWLA</span>
            <h2 className="font-serif text-[clamp(1.4rem,3vw,2.2rem)] text-[#F5F5F5] mb-6">Dlaczego warto kupic szczenie z odpowiedzialnej hodowli</h2>
            <div className="w-12 h-px bg-[#D4AF37]/30 mb-8" />
            <div className="space-y-5 text-sm text-[#B8B8B8] leading-relaxed">
              <p>Zakup szczeniecia z odpowiedzialnej hodowli daje wieksza pewnosc co do pochodzenia, zdrowia i warunkow chowu. Odpowiedzialni hodowcy stawiaja na psychike, zdrowie i prawidlowy rozwoj juz od pierwszych tygodni zycia, a nie jedynie na wyglad.</p>
              <p>Szczenieta sa zadbane, zsocjalizowane i przygotowane do zycia w nowym domu. Wlasciciele otrzymuja dokumenty rodowodowe i kontakt z hodowca. To szczegolnie wazne w przypadku rasy tak wymagajacej, inteligentnej i silnie zwiazanej z czlowiekiem, jaka jest owczarek niemiecki.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Na co zwrocic uwage */}
      <section id="poradnik" className="px-6 md:px-12 py-20 md:py-24">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <span className="text-[10px] tracking-[0.3em] text-[#D4AF37] block mb-4">PORADNIK</span>
            <h2 className="font-serif text-[clamp(1.4rem,3vw,2.2rem)] text-[#F5F5F5] mb-6">Na co zwrocic uwage przy wyborze szczeniecia</h2>
            <div className="w-12 h-px bg-[#D4AF37]/30 mb-8" />
            <div className="space-y-5 text-sm text-[#B8B8B8] leading-relaxed">
              <p>Przy wyborze szczeniecia owczarka niemieckiego warto zwrocic uwage przede wszystkim na jakosc hodowli, a nie jedynie na wyglad psa. Kluczowe sa badania zdrowotne rodzicow, rodowod, warunki chowu miotu, socjalizacja szczeniat oraz podejscie hodowcy do charakteru i uzytkowosci.</p>
              <p>Warto pytac o zdrowie rodzicow, charakter linii hodowlanej, sposob zywienia, opieke weterynaryjne, dokumenty i predyspozycje szczeniat.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="bg-[#141416] px-6 md:px-12 py-20 md:py-24 border-y border-[#2A2A2D]">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-12">
            <span className="text-[10px] tracking-[0.3em] text-[#D4AF37] block mb-4">FAQ</span>
            <h2 className="font-serif text-[clamp(1.4rem,3vw,2.2rem)] text-[#F5F5F5] mb-6">Najczesciej zadawane pytania</h2>
            <div className="w-12 h-px bg-[#D4AF37]/30" />
          </motion.div>
          <div className="space-y-3" data-testid="faq-accordion">
            {faqData.map((item, idx) => (
              <FAQItem key={idx} idx={idx} q={item.q} a={item.a} isOpen={openFaq === idx} onToggle={() => setOpenFaq(openFaq === idx ? null : idx)} />
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="px-6 md:px-12 py-24 md:py-28">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <TypeWriterText />
            <p className="text-[#B8B8B8] text-sm md:text-base leading-relaxed mb-10 max-w-2xl mx-auto">
              Szukasz szczeniecia owczarka niemieckiego z odpowiedzialnej hodowli? Poznaj nasze psy, mioty i skontaktuj sie z nami, aby dowiedziec sie wiecej o hodowli Wolfe vom Katzenberg.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/psy" data-testid="breed-cta-dogs"
                className="px-10 py-3.5 bg-[#D4AF37] text-[#0B0B0D] text-xs font-bold tracking-[0.2em] hover:bg-[#C8A96A] transition-all duration-300 hover:shadow-lg hover:shadow-[#D4AF37]/20 flex items-center gap-2"
              >ZOBACZ NASZE PSY <ArrowRight className="w-3.5 h-3.5" /></Link>
              <Link to="/mioty" data-testid="breed-cta-litters"
                className="px-10 py-3.5 border border-[#D4AF37] text-[#D4AF37] text-xs tracking-[0.2em] hover:bg-[#D4AF37] hover:text-[#0B0B0D] transition-all duration-300 flex items-center gap-2"
              >ZOBACZ AKTUALNE MIOTY <ArrowRight className="w-3.5 h-3.5" /></Link>
              <Link to="/anatomia-owczarka-niemieckiego" data-testid="breed-cta-anatomy"
                className="px-10 py-3.5 border border-[#2A2A2D] text-[#B8B8B8] text-xs tracking-[0.2em] hover:border-[#D4AF37]/40 hover:text-[#D4AF37] transition-all duration-300 flex items-center gap-2"
              >ANATOMIA RASY <ArrowRight className="w-3.5 h-3.5" /></Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default KennelBreed;
